/**
 * Support nodes (04 §2.2) resolve at once, with no gameplay: a small dialog states what the
 * node does and offers its choices; the pick goes to `GameApi.visitSupportNode` and the line it
 * returns is shown before the map is rebuilt.
 *
 * What the node offers comes from `describe(node.id)` when the API knows the node (the real one
 * does: it lists a depot's three candidates by name, numbered from 0 like the `choice` index),
 * else from `describe(kind)`. A choice may be named by `describe('choice:<kind>:<i>')`; without
 * that the buttons read `OPTION (i)`, matching the numbering in the text.
 */

import type { NodeKind, TheatreNode } from '../../../meta/types';
import type { Ctx } from '../context';
import { box, button, el, errorMessage } from '../dom';
import { isDescribed } from '../text';
import { dialog, notice } from '../widgets/confirm';

/** How many choices each support kind offers (04 §2.2). Missions and rescues are not dialogs. */
export const SUPPORT_CHOICES: Readonly<Record<NodeKind, number>> = {
  mission: 0,
  rescue: 0,
  rest: 1,
  supply: 2,
  depot: 3,
  intercept: 2,
};

/** A depot may be walked past: choice -1 takes nobody on. */
const DECLINE_CHOICE = -1;

/** The id a choice is described under: `choice:supply:0`, `choice:supply:1`... */
export function choiceId(kind: NodeKind, index: number): string {
  return `choice:${kind}:${index}`;
}

/** Opens the dialog for an open support node; `onResolved` runs after the player reads the result. */
export function openSupportDialog(ctx: Ctx, node: TheatreNode, onResolved: () => void): void {
  const raw = (id: string): { name: string; text: string } => ctx.api.describe(id);
  const about = ctx.describe(node.kind);
  const offer = isDescribed(raw, node.id) ? ctx.describe(node.id).text : about.text;
  const d = dialog(about.name, () => ctx.closeModal());
  if (offer !== '') d.body.append(box('menu-prose', offer));
  const error = el('div', 'menu-error menu-hidden');
  d.body.append(error);

  const choose = (index: number): void => {
    let line: string;
    try {
      line = ctx.api.visitSupportNode(node.id, index);
    } catch (err) {
      error.textContent = errorMessage(err);
      error.classList.remove('menu-hidden');
      return;
    }
    notice(ctx, about.name, [line === '' ? 'DONE.' : line], onResolved);
  };

  const count = Math.max(1, SUPPORT_CHOICES[node.kind]);
  const buttons: HTMLButtonElement[] = [];
  for (let i = 0; i < count; i++) {
    const id = choiceId(node.kind, i);
    const known = isDescribed(raw, id);
    const label = known ? ctx.describe(id).name : count === 1 ? 'PROCEED' : `OPTION (${i})`;
    const b = button(label, () => choose(i));
    if (known) b.title = ctx.describe(id).text;
    buttons.push(b);
  }
  if (node.kind === 'depot') buttons.push(button('TAKE NOBODY', () => choose(DECLINE_CHOICE)));
  d.actions.append(button('NOT NOW', () => ctx.closeModal()), ...buttons);
  ctx.openModal({ ...d.modal, initialFocus: () => buttons[0] ?? null });
}
